import React, { useMemo } from 'react';

import noop from 'lodash/noop';

import { isAgentHubEnabled } from '@lumapps/agent-hub/ducks/selectors';
import { SearchDocumentType } from '@lumapps/analytics-tracking/types';
import { canUseAskAiFeature } from '@lumapps/ask-ai/ducks/selectors';
import { classnames, visuallyHidden } from '@lumapps/classnames';
import { get as getConstants } from '@lumapps/constants';
import { notifySubscribers } from '@lumapps/customizations/api';
import { Events } from '@lumapps/customizations/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { searchSparkles } from '@lumapps/lumx/custom-icons';
import { mdiMagnify } from '@lumapps/lumx/icons';
import { Emphasis, FlexBox, IconButton, Theme } from '@lumapps/lumx/react';
import { permissionsSelector } from '@lumapps/permissions';
import { QuickSearchEntity } from '@lumapps/quick-search/types';
import { addInteractionSuggestion } from '@lumapps/quick-search/utils/suggestionsUtils';
import { useSelector } from '@lumapps/redux/react';
import { AppId } from '@lumapps/router';
import { sendSearchClickBatchedEvents } from '@lumapps/search/api/analytics';
import { CUTOMIZATION_EVENTS_SEARCH_CAUSE, SEARCH_RESULT_TYPES } from '@lumapps/search/constants';
import { SearchSuggestion, SearchSuggestionType } from '@lumapps/search/types';
import { useTopStyles } from '@lumapps/style/hooks/useTopStyles';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { usePrevious } from '@lumapps/utils/hooks/usePrevious';
import { generateUUID } from '@lumapps/utils/string/generateUUID';

import { useSearchBox } from '../../hooks/useSearchBox';
import { SearchBoxCombobox } from '../SearchBoxCombobox';

/**
 * The general namespace for this component.
 *
 * @type {string}
 * @constant
 */
const NAMESPACE = 'searchbox';
const SEARCH_BOX_ID = 'main-search-box';

/**
 * The initial state for the searchbox. It defaults to empty
 * but in case we want to change it in the future, we can pass it as a prop.
 *
 * @type {string}
 * @constant
 */
const INITIAL_SEARCH_QUERY = '';

export interface SearchBoxProps {
    instanceId: string;
    /** True if the autocomplete should be displayed */
    autocompleteEnabled?: boolean;
    /** True if the suggestions are still being loaded. */
    isLoading?: boolean;
    /** The callback to be executed once the user changes the query string. */
    onQueryChange?: (s: string, autocompleteEnabled: boolean) => void;
    /** The callback to be executed once the user wants to perform a search. */
    onSelect?: (suggestion: SearchSuggestion) => void;
    /** String that will be used to initialize the text field. */
    searchQuery?: string;
    /** List of suggestions to be displayed. */
    suggestions?: SearchSuggestion[];
    /** Custom? className */
    className?: string;
    /** should the focus on the searchbox input be forced */
    forceFocus?: boolean;
    /** theme override */
    theme?: Theme;
    /** Custom placeholder */
    placeholder?: string;
    /** should we display the suggestions list? */
    showSuggestions?: boolean;
    /** organisation id */
    isUserConnected: boolean;
    /** are we in mobile display? */
    isMobile?: boolean;
    /** Callback that will be triggered when a delete button is clicked upon. */
    onDeleteSuggestion?: (suggestion: SearchSuggestion) => void;
    /** Callback when the box is focused */
    onFocus?: (isFocus: boolean) => void;
    /** callback when an interaction item is clicked */
    addQuickSearchInteraction?: (item: QuickSearchEntity, searchQuery: string, shouldSendQuery: boolean) => void;
}

/**
 * Renders the Searchbox component for the main navigation.
 */
const SearchBox: React.FC<SearchBoxProps> = ({
    onSelect = noop,
    searchQuery = INITIAL_SEARCH_QUERY,
    onQueryChange = noop,
    suggestions = [],
    isLoading = false,
    className = '',
    forceFocus = false,
    theme,
    placeholder,
    showSuggestions = false,
    onDeleteSuggestion,
    onFocus = noop,
    isUserConnected,
    isMobile = false,
    addQuickSearchInteraction = addInteractionSuggestion,
    instanceId,
}) => {
    const { get } = useDataAttributes('search');
    const searchBoxId = useMemo(() => generateUUID(SEARCH_BOX_ID), []);

    const { applicationId } = getConstants();
    const isWebview = applicationId === AppId.webview;

    /**
     * Basic setup for the searchbox component.
     * - suggestion visibility: manages whether the suggestions should be displayed or not.
     * - was search triggered: allows to control when a search was triggered and trigger searches on certain events.
     * We could have this state managed by Redux, but we will define it here since it is more of
     * a UI related state, and not related to data. Hence, it made more sense to leave it here.
     */
    const { translateKey } = useTranslate();
    const inputRef = React.useRef(null);
    const topStyles = useTopStyles();
    const canUseAskAi = useSelector(canUseAskAiFeature);
    const canUseAgentHub = useSelector(isAgentHubEnabled);
    const hasPermission = useSelector(permissionsSelector).authorizations.canReadAiAugmentedSearch;
    const canUseAskAiOrAgentHub = (canUseAskAi || canUseAgentHub) && hasPermission;

    /**
     * Check that the user has the permissions to use AskAi or AgentHub search feature
     * If so, change the default placeholder and the button's icon
     */
    const defaultPlaceholder = canUseAskAiOrAgentHub
        ? translateKey(GLOBAL.ASK_QUESTION_OR_SEARCH)
        : translateKey(GLOBAL.SEARCH);
    const submitButtonIcon = canUseAskAiOrAgentHub ? searchSparkles : mdiMagnify;

    React.useEffect(() => {
        window.SEARCH_INPUT = inputRef;

        if (forceFocus) {
            setTimeout(() => {
                if (inputRef && inputRef.current) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [forceFocus]);

    const { handleQueryChange, handleSuggestionSelected, triggerSearchQuery } = useSearchBox({
        query: searchQuery,
        isUserConnected,
        instanceId,
        onSearchTrigger: (query) => {
            onQueryChange(query);
            if (onSelect) {
                onSelect({ query });
            }
        },
        onQueryChange,
        onFocusChange: onFocus,
        scrollToTopOnSearch: true,
        addQuickSearchInteraction,
    });

    /**
     * Upon closing the Autocomplete (a click outside can trigger this for example),
     * we need to close the dropdown, and we can do that by calling `onFocus` and pass `false`.
     */
    const onClose = () => {
        onFocus(false);
    };

    /**
     * Function triggered when the value on the text field is changed. It will trigger the `onQueryChange`
     * and enable/disable the suggestions visibility accordingly.
     * @param {string} value Value changed from the text field.
     */
    const onChange = (value: string) => {
        handleQueryChange(value);
        if (value && !showSuggestions) {
            onFocus(true);
        }
    };

    /** Function triggered when a suggestion is selected
     * it will call the tracking endpoint and send an event of type click_action
     * TODO: add search_click and search events in the sent batched events
     * */

    const trackSuggestionsClick = (suggestion: SearchSuggestion) => {
        const { label, item } = suggestion;

        if (item?.url && item?.entityId && item?.entityType === SEARCH_RESULT_TYPES.DIRECTORY_ENTRY) {
            sendSearchClickBatchedEvents({
                clickAction: {
                    targetId: item.entityId,
                    targetType: SearchDocumentType.DIRECTORY_ENTRY,
                    url: item.url,
                    thumbnail: item.thumbnail,
                    title: label,
                },
            });
        }
    };

    /**
     * Function triggered when a suggestion is selected from the list of suggestions. This function will also be called
     * when the ENTER key is pressed while navigating an item
     * @param {Object} suggestion       Suggestion presented on the autocomplete.
     * @param {string} suggestion.query Suggestion text presented on the autocomplete.
     */
    const onSuggestionSelected = (suggestion: SearchSuggestion) => {
        if (
            (suggestion.type === SearchSuggestionType.SUGGESTION ||
                suggestion.type === SearchSuggestionType.INTERACTION) &&
            suggestion.item
        ) {
            trackSuggestionsClick(suggestion);
        }

        handleSuggestionSelected(suggestion);
    };

    const autocompleteClassName = classnames(NAMESPACE, className, {
        [`${NAMESPACE}--theme-${theme || topStyles.theme}`]: Boolean(theme) || Boolean(topStyles.theme),
        [`${NAMESPACE}--is-mobile`]: isMobile || isWebview,
    });

    /**
     * Triggered when the form is submitted
     * @param {Object} evt Form submit event
     */
    const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        triggerSearchQuery(searchQuery);
    };

    /** remove from suggestions the item list which contains too many unstable info */
    const cleanedSuggestions = React.useMemo(
        () => suggestions.map(({ counterClick, label, type, siteId }) => ({ counterClick, label, type, siteId })),
        [suggestions],
    );

    const wasLoading = usePrevious(isLoading);

    // send event to customization API when query is updated and suggestions displayed
    React.useEffect(() => {
        if (wasLoading && !isLoading && showSuggestions) {
            notifySubscribers(
                {
                    query: searchQuery,
                    suggestions: cleanedSuggestions,
                    cause: CUTOMIZATION_EVENTS_SEARCH_CAUSE.SEARCHBOX_INTERACTION,
                },
                Events.SEARCH,
            );
        }
    }, [cleanedSuggestions, isLoading, searchQuery, showSuggestions, wasLoading]);

    return (
        <FlexBox
            as="form"
            role="search"
            onSubmit={handleSubmit}
            orientation="horizontal"
            // eslint-disable-next-line lumapps/no-classname-strings
            className={`${NAMESPACE}__wrapper`}
        >
            <label className={visuallyHidden()} htmlFor={searchBoxId}>
                {placeholder || defaultPlaceholder}
            </label>
            <SearchBoxCombobox
                inputRef={inputRef}
                suggestions={suggestions}
                onDeleteSuggestion={onDeleteSuggestion}
                onSuggestionSelected={onSuggestionSelected}
                isLoading={isLoading}
                triggerSearchQuery={triggerSearchQuery}
                onClose={onClose}
                theme={theme}
                className={autocompleteClassName}
                id={searchBoxId}
                searchQuery={searchQuery || ''}
                placeholder={placeholder || defaultPlaceholder}
                onQueryChange={onChange}
                onFocus={onFocus}
                onOpen={() => {
                    if (!showSuggestions) {
                        onFocus(true);
                    }
                }}
            />
            <IconButton
                theme={theme}
                label={placeholder || defaultPlaceholder}
                emphasis={Emphasis.medium}
                icon={submitButtonIcon}
                className={classnames(`${NAMESPACE}__submit-btn`, {
                    [`${NAMESPACE}__submit-btn--is-mobile`]: isMobile || isWebview,
                })}
                type="submit"
                {...get({ element: 'submit' })}
            />
        </FlexBox>
    );
};

export { SearchBox };
